import type { DeepReadonly } from 'vue';

import type {
  HotelIngredientAdapter,
  OwnStayIngredientAdapter,
  OwnTransportIngredientAdapter,
  SearchTerm,
  TransportIngredientAdapter,
} from '../tripPlanner';
import type { Pax, RecipeMetaData } from '../general';
import type { IngredientId } from '../brandedTypes/ingredientId';

import type {
  HotelConstraintQL,
  HotelConstraintSelectionQL,
} from './ingredientSchemas';
import type { InputPaxGroupQL } from './graphql_generated/graphql';

export type TripSearchCriteria = ItinerarySearchCriteria;

export type BusinessCriteria = {
  affiliateId?: string;
  metaData?: RecipeMetaData;
  searchForPackageRates?: boolean | null;
  avoidDateShift?: boolean;
  searchOnlyLiveSelectedDay?: boolean;
};

export interface ItinerarySearchUserCriteria {
  // NOTE: Ingredients will be set to null if we don't want to search on BE
  // i.e. when user deletes airport when editing flight ingredient
  ingredients:
    | (
        | OwnStayIngredientAdapter
        | HotelIngredientAdapter
        | TransportIngredientAdapter
        | OwnTransportIngredientAdapter
      )[]
    | null;
  startSearchDate?: Date;
  endSearchDate?: Date;
  selectedDate?: Date;
  pax: DeepReadonly<Pax>;
  sessionId: string;
  skipCache?: boolean;
}

export type ItinerarySearchUserCriteriaDependencies = Omit<
  ItinerarySearchUserCriteria,
  'sessionId'
>;

export interface ItinerarySearchCriteria {
  userCriteria: ItinerarySearchUserCriteria;
  businessCriteria: BusinessCriteria;
  shouldCancelPreviousOngoingRequest?: boolean;
  numberOfHotelSuggestions?: number;
  priceAccuracyToken?: string;
}

export interface DestinationAvailabilitySearchUserCriteria {
  recipe: ItinerarySearchUserCriteria;
  ingredientIndex: number;
  day?: Date;
  constraint: HotelConstraintSelectionQL;
}

export interface DestinationAvailabilityCriteria {
  userCriteria: DestinationAvailabilitySearchUserCriteria;
  businessCriteria: BusinessCriteria;
}

export interface ActivityAvailabilityUserCriteria {
  recipe: ItinerarySearchUserCriteria;
  productCode: string;
  parentIngredientId: IngredientId;
  ingredientIndex: number;
}

export interface ActivityAvailabilityCriteria {
  userCriteria: ActivityAvailabilityUserCriteria;
  businessCriteria: BusinessCriteria;
}

export interface ActivitiesInDestinationCriteria {
  ingredientId: IngredientId;
  searchTerm: SearchTerm;
  nearbyPolygonText?: string;
  nearbyPolygonId?: number;
}

export interface AccommodationAvailabilityUserCriteria {
  recipe: ItinerarySearchUserCriteria;
  ingredientIndex: number;
  productCode: string;
  day?: Date;
}

export interface AccommodationAvailabilityCriteria {
  userCriteria: AccommodationAvailabilityUserCriteria;
  businessCriteria: BusinessCriteria;
}

export interface TransportAvailabilityUserCriteria {
  recipe: ItinerarySearchUserCriteria;
  ingredientIndex: number;
  day: Date;
  elementsInPage: number;
  page: number;
}

export interface TransportAvailabilityCriteria {
  userCriteria: TransportAvailabilityUserCriteria;
  businessCriteria: BusinessCriteria;
}

export enum AvailabilityOrderQL {
  None = 'NONE',
  Cheapest = 'CHEAPEST',
  Popular = 'POPULAR',
}

export interface DestinationAvailabilityRecipeQL extends HotelConstraintQL {
  startDate: Date | string;
  endDate: Date | string;
  pax: InputPaxGroupQL[];
  market: string;
  numberOfNights?: number;
  skipCache?: boolean;
  affiliateId?: string;
  featureToggles?: string[];
}

export interface ActivitySuggestionsSearchUserCriteria {
  recipe: ItinerarySearchUserCriteria;
  ingredientIndex: number;
  day: Date;
  constraint: HotelConstraintSelectionQL;
}

export interface ActivitySuggestionsSearchCriteria {
  userCriteria: ActivitySuggestionsSearchUserCriteria;
  businessCriteria: BusinessCriteria;
}

export interface DestinationSuggestedActivitiesSearchCriteria {
  destinations: DestinationSuggestedActivitySearchCriteria[];
}

export interface DestinationSuggestedActivitySearchCriteria {
  constraint: HotelConstraintQL;
  ingredientId: IngredientId;
}
